<template>
  <a-modal
    id="req-annotation-note-modal"
    v-model:visible="showTrainingNoteModal"
    title="Label Model Version"
    destroy-on-close
    :ok-button-props="{ id: 'req-note-modal-ok-btn' }"
    :cancel-button-props="{
      id: 'req-note-modal-cancel-btn',
      style: { marginLeft: 'auto' },
    }"
  >
    <template #closeIcon>
      <close-outlined id="req-train-note-close-btn" />
    </template>
    <a-textarea
      v-model:value="additionalNote"
      placeholder="Add something about Train Model (Optional)"
      :rows="4"
      allow-clear
    />
    <template #footer>
      <div class="d-flex w-100 justify-content-end">
        <a-button
          id="save-annotation-progress-btn"
          type="primary"
          :disabled="!additionalNote"
          :loading="isSetToTraining"
          @click="startTraining"
        >
          Save
        </a-button>
      </div>
    </template>
  </a-modal>
  <RequestTrainingModal
    v-model:show-setup-training-modal="showSetupTrainingModal"
    @set-show-setup-training-modal="setShowSetupTrainingModal"
    @perform-training="performTraining"
  />
  <a-row :gutter="[16, 29]" class="py-4 px-2 p-md-4">
    <a-col :xs="20" :sm="8" :md="8" :lg="8" :xl="8">
      <a-select
        id="train-model-select-task"
        ref="train_task_select"
        class="w-100"
        :value="selectedTask"
        show-search
        placeholder="Select Operation"
        :options="options"
        :filter-option="filterOption"
        :loading="isFetchingTasks"
        @change="handleTaskSelect"
      ></a-select>
    </a-col>
    <a-col :xs="12" :sm="8" :md="8" :lg="8" :xl="8" class="d-none d-sm-block">
      <a-button
        type="primary"
        class="lg-button"
        :loading="trainingBegin"
        :disabled="!selectedTask"
        @click="startTraining"
      >
        Request Training
      </a-button>
    </a-col>

    <!-- mobile screen -->
    <a-col :xs="3" class="d-block d-sm-none">
      <div class="demo-dropdown-wrap">
        <a-dropdown-button
          :trigger="['click']"
          placement="bottomRight"
          class="float-right"
        >
          <template #overlay>
            <a-menu :selected-keys="currentItem">
              <a-menu-item key="training">
                <a-button
                  type="primary"
                  class="w-100"
                  style="width: 100%"
                  :disabled="!selectedTask"
                  @click="startTraining"
                >
                  Request Training
                </a-button>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown-button>
      </div>
    </a-col>

    <a-col span="24">
      <a-tabs v-model:activeKey="tabActiveKeys">
        <a-tab-pane id="tasks-trained-tab" key="trained">
          <template #tab>
            <a-typography-text strong>
              Operations Trained: &nbsp;
              <a-tag id="trained-task-count" color="blue">
                {{ modelsList.length }}
              </a-tag>
            </a-typography-text>
          </template>

          <a-table
            :columns="columns"
            :data-source="modelsList"
            :pagination="pagination"
            bordered
            :loading="loadingModelList"
            :scroll="{ y: 600, x: true }"
            @change="handleTableChange"
          >
            <template #bodyCell="{ column, text }">
              <template v-if="column.dataIndex === 'startTrainingTime'">
                <span>
                  {{ new Date(text).toDateString() }}
                </span>
              </template>
              <span v-if="column.dataIndex === 'status_update_time'">
                <small>
                  {{ formatDate(text) }}
                </small>
              </span>
            </template>
          </a-table>
        </a-tab-pane>
        <a-tab-pane id="task-under-training-tab" key="inAnnotation">
          <template #tab>
            <a-typography-text strong>
              Operations Queued for Training: &nbsp;
              <a-tag id="tasks-under-training-count" color="blue">
                {{ tasksBeingLabeled.length }}
              </a-tag>
            </a-typography-text>
          </template>
          <a-table
            :columns="columnsForTaskBeingTrained"
            :data-source="tasksBeingLabeled"
            :loading="isLoadingTasksBeingLabeled"
            bordered
            :pagination="{ position: ['bottomCenter'] }"
            :scroll="{ y: 600, x: true }"
          >
            <template #bodyCell="{ column, text, record }">
              <template v-if="column.dataIndex === 'startTrainingTime'">
                <span>
                  {{ new Date(text).toDateString() }}
                </span>
              </template>
              <span v-if="column.dataIndex === 'status_update_time'">
                <small>
                  {{ formatDate(text) }}
                </small>
              </span>
              <span v-if="column.dataIndex === 'actions'">
                <a-button
                  type="danger"
                  @click="cancelTrainingTask(record.taskId, record.id)"
                >
                  Cancel
                </a-button>
              </span>
            </template>
          </a-table>
        </a-tab-pane>
        <a-tab-pane id="task-failed-tab" key="Failed">
          <template #tab>
            <a-typography-text strong>
              Operations Failed: &nbsp;
              <a-tag id="tasks-failed-count" color="blue">
                {{ tasksFailed.length }}
              </a-tag>
            </a-typography-text>
          </template>
          <a-table
            :columns="columnsForTaskFailed"
            :data-source="tasksFailed"
            :loading="isLoadingTasksFailed"
            bordered
            :pagination="{ position: ['bottomCenter'] }"
            :scroll="{ y: 600, x: true }"
          >
            <template #bodyCell="{ column, record, text }">
              <span v-if="column.dataIndex === 'status_update_time'">
                <small>
                  {{ formatDate(text) }}
                </small>
              </span>
              <span v-else-if="column.dataIndex === 'fail_reason'">
                {{ text }}
                <a-button
                  v-if="record.detail_error"
                  type="link"
                  @click="showErrorModal(record.detail_error)"
                >
                  <template #icon>
                    <info-circle-outlined />
                  </template>
                </a-button>
              </span>
            </template>
          </a-table>
          <!-- Modal for Detail Error -->
          <a-modal
            v-model:visible="errorModalVisible"
            title="Error Details"
            :footer="null"
            @ok="errorModalVisible = false"
          >
            <p>{{ selectedError }}</p>
          </a-modal>
        </a-tab-pane>
      </a-tabs>
    </a-col>
  </a-row>
  <OperationProgress
    v-if="trainingProgress.length"
    :process-list="trainingProgress"
    :process-type="'Train Model'"
    :show-close="true"
    @close-progress="cancelTask"
  />
</template>
<script>
import TaskService from '@/services/tasks';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons-vue';
import OperationProgress from 'src/components/shared/Components/OperationProgress.vue';
import { roles } from 'src/config/roles-config';
import trainingStatuses from 'src/config/training-status-config.js';
import spaceMixin from 'src/mixins/handleSpace';
import LRTService from 'src/services/longRunningTask';
import NotificationService from 'src/services/notification';
import { getSortedTask } from 'src/utils/task';
import { mapActions, mapGetters } from 'vuex';
import dateHelper from '../../../shared/Helpers/dateHelper';
import { longRunningTaskStatus } from '../process/ObjectDetection/config';
import RequestTrainingModal from './RequestTrainingModal.vue';
import RequestTrainingModel from './trainingModel';
export default {
  components: {
    CloseOutlined,
    InfoCircleOutlined,
    RequestTrainingModal,
    OperationProgress,
  },
  mixins: [spaceMixin],
  inject: ['toast'],
  data: function () {
    return {
      trainingBegin: false,
      activeKey: '1',
      loadingModelList: false,
      tasks: [],
      isLoadingTasks: false,
      reqAnnotation: true,
      modelsList: [],
      interval: null,
      trainingProgress: [],
      stoppingDuraion: 60,
      task_id: null,
      currentPage: 1,
      visiblePopConfirm: false,
      tabActiveKeys: 'trained',
      tasksBeingLabeled: [],
      isLoadingTasksBeingLabeled: false,
      tasksFailed: [],
      isLoadingTasksFailed: false,
      currentItem: ['training'],
      showTrainingNoteModal: false,
      showSetupTrainingModal: false,
      additionalNote: '',
      isSetToTraining: false,
      setUpModelParameter: {},
      columns: [
        {
          title: 'Model name',
          dataIndex: 'taskName',
        },
        {
          title: 'Start time',
          dataIndex: 'status_update_time',
        },
      ],
      columnsForTaskBeingTrained: [
        {
          title: 'Task name',
          dataIndex: 'taskName',
        },
        {
          title: 'Start time',
          dataIndex: 'status_update_time',
        },
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'action',
        },
      ],
      columnsForTaskFailed: [
        {
          title: 'Task name',
          dataIndex: 'taskName',
        },
        {
          title: 'Status Update Time',
          dataIndex: 'status_update_time',
        },
        {
          title: 'Failed Reason',
          dataIndex: 'fail_reason',
        },
      ],
      errorModalVisible: false,
      selectedError: '',
    };
  },

  computed: {
    ...mapGetters([
      'taskName',
      'taskDetails',
      'organization',
      'allTasks',
      'selectedTask',
      'isFetchingTasks',
      'taskJson',
      'modelChoice',
    ]),

    options() {
      return getSortedTask(this.allTasks);
    },

    getModelsList() {
      return this.modelsList;
    },

    modelsListLength() {
      return this.modelsList.length;
    },
    pagination() {
      return {
        total: this.modelsList.length,
        current: this.currentPage,
        showSizeChanger: true,
        position: ['bottomCenter'],
      };
    },
  },

  watch: {
    selectedTask(value) {
      if (!value) return;
      this.fetchTaskDetails(value);
    },

    taskDetails(value) {
      value && this.setTaskNameAndProcess(value);
    },
  },

  created() {
    this.getAllTasks();
    this.getTrainedModels();
    this.isModelTraining();
    this.getTasksBeingAnnotated();
    this.getTasksFailed();
    this.interval = setInterval(this.pollForTaskStatus, 10 * 1000);
  },

  beforeUnmount() {
    clearInterval(this.interval);
  },

  methods: {
    ...mapActions([
      'getAllTasks',
      'setSelectedTask',
      'fetchTaskDetails',
      'setTaskNameAndProcess',
      'getTaskObjects',
    ]),

    handleTaskSelect(val) {
      this.setSelectedTask(val);
      this.$refs.train_task_select.blur();
      this.getTaskObjects(this.selectedTask);
    },

    handleTableChange(pag) {
      const { current } = pag;
      this.currentPage = current;
    },

    pollForTaskStatus() {
      this.getTrainedModels();
      this.isModelTraining();
      this.getTasksBeingAnnotated();
      this.getTasksFailed();
    },

    async getTrainedModels() {
      this.loadingModelList = true;
      const [error, data] = await TaskService.fetchTrainedTasksByStatus(
        trainingStatuses.trained
      );
      if (error) return;
      this.modelsList = data?.map(({ task, ...model }) => ({
        ...model,
        taskId: task.id,
        taskName: task.taskName,
      }));
      this.loadingModelList = false;
    },

    async getTrainingStatus(taskId) {
      let [error, data] = await TaskService.fetchTaskStatus(taskId);
      if (error) return;
      if (data.train_status === trainingStatuses.trained) {
        this.getTrainedModels();
        return;
      }
    },

    async getLongRunningTaskStatus() {
      const [error, data] = await LRTService.getLongRunningTasks(false, {
        name: 'object-detection-task',
        status: longRunningTaskStatus.STARTED,
      });
      this.trainingProgress = [];
      this.getStartedTrainingProgress(data);
    },

    getStartedTrainingProgress(started) {
      if (!Array.isArray(started) || started.length === 0) return;

      const taskMap = new Map(
        this.allTasks?.map((task) => [task.id, task.taskName])
      );
      started.forEach(({ id, task, details }) => {
        if (taskMap.get(task) === undefined) return;
        const taskName = taskMap.get(task) || '';
        const name = `Training Object Detection Model ${taskName}`;

        this.trainingProgress.push({
          id,
          name,
          status: longRunningTaskStatus.STARTED,
          progress: details.progress,
          task,
        });
      });
    },

    async isModelTraining() {
      const [error, data] = await TaskService.fetchTrainedTasksByStatus(
        trainingStatuses.training
      );
      if (error || !data.length) {
        this.trainingProgress = [];
        this.getTrainedModels();
        return;
      }
      this.getLongRunningTaskStatus();
    },

    async getTasksBeingAnnotated() {
      this.isLoadingTasksBeingLabeled = true;
      const [error, data] = await TaskService.fetchTrainedTasksByStatus(
        trainingStatuses.in_annotation
      );
      this.isLoadingTasksBeingLabeled = false;
      if (error) return;
      this.tasksBeingLabeled = data?.map(({ task, ...model }) => ({
        ...model,
        taskId: task.id,
        taskName: task.taskName,
      }));
    },
    async getTasksFailed() {
      this.isLoadingTasksFailed = true;
      const [error, data] = await TaskService.fetchTrainedTasksByStatus(
        trainingStatuses.failed
      );
      this.isLoadingTasksFailed = false;
      if (error) return;
      this.tasksFailed = data?.map(({ task, ...model }) => ({
        ...model,
        taskId: task.id,
        taskName: task.taskName,
      }));
    },

    async sendReqAnnotationMail(taskName, taskId) {
      const data_dict = {
        recipient_email: ['support@retrocausal.ai'],
        sender_email: 'postman@retrocausal.ai',
        subject: `Annotation Requested for Task: ${taskName}`,
        body: `Annotation Requested\n\nOrganization: ${this.organization}\nTask Id: ${taskId}\nTask Name: ${taskName}`,
        topic_name: 'General',
      };

      const [error] = await NotificationService.sendNotificationEmail(
        data_dict
      );
      if (error) {
        console.log(error);
      }
    },

    handleVisibleChange() {
      const flag = this.modelsList.map((e) => e.id).includes(this.selectedTask);
      if (flag) {
        this.visiblePopConfirm = true;
      } else {
        this.startTraining();
      }
    },

    async handleCheckTrainingStart() {
      const { selectedTask: taskId } = this;
      const [error, data] = await TaskService.fetchTaskStatus(taskId, false);
      if (error) return;
      const { train_status } = data;
      if (
        train_status === trainingStatuses.in_annotation ||
        train_status === trainingStatuses.training
      ) {
        this.toast.info('This task is already in under training!');
        return;
      }
      this.showTrainingNoteModal = true;
    },

    setShowSetupTrainingModal(value) {
      this.showSetupTrainingModal = value;
    },

    setSetupModelParameter(payload) {
      this.setUpModelParameter = payload;
    },

    async startTraining() {
      if (!this.selectedTask) {
        this.toast.info('Please select a task first!');
        return;
      }
      // Store getter, not localStorage, so "View as customer" applies.
      const user = this.$store.getters.role;
      if (user !== roles.support_user) {
        this.trainingBegin = true;
        await this.performTraining();
      } else this.showSetupTrainingModal = true;
      this.trainingBegin = false;
    },

    async performTraining(data = {}) {
      const requestTrainingModel = new RequestTrainingModel(
        this.taskJson,
        this.organization,
        this.modelChoice
      );
      const result = await requestTrainingModel.requestTraining(
        this.selectedTask,
        data
      );
      if (result.status) {
        this.toast.success(result.message);
        this.getTasksBeingAnnotated();
        this.getTasksFailed();
      } else this.toast.error(result.message);
    },

    getTrainingPayload() {
      const formData = new FormData();
      const { id: taskId, taskName } = this.tasks.find(
        (el) => el.id === this.selectedTask
      );
      formData.append('task_name', taskName);
      formData.append('task_id', taskId);
      formData.append('annotation', this.reqAnnotation ? 1 : 0);
      formData.append('organization', this.organization);
      return formData;
    },

    async stopTraining() {
      const { taskName } = this.tasks.find((t) => t.id === this.selectedTask);
      const payload = {
        organization: this.organization,
        task_name: taskName || this.taskName,
      };

      const [error] = await TaskService.stopTrainingOfTask(payload, false);
      if (error) {
        this.toast.error('Error in stop training!');
        return;
      }
    },

    formatDate(date) {
      return dateHelper.formatDate(date);
    },

    async cancelTask(longRunningTaskId) {
      const item = this.trainingProgress.find(
        (obj) => obj.id === longRunningTaskId
      );
      if (!item) return;
      await this.cancelTrainingTask(item.task, longRunningTaskId);
    },

    async cancelTrainingTask(taskId, longRunningTaskId) {
      const payload = {
        fail_reason: 'Cancelled by User',
        train_status: trainingStatuses.failed,
      };
      const [error] = await TaskService.updateTrainedTask(
        taskId,
        payload,
        true
      );
      if (error) {
        this.toast.error('Error occured in deleting record');
        return;
      }
      this.toast.info('Training Cancelled');
      this.trainingProgress = this.trainingProgress.filter(
        (obj) => obj.id !== longRunningTaskId
      );
      this.getTasksBeingAnnotated();
      this.getTasksFailed();
      this.getTrainedModels();
    },

    showErrorModal(error) {
      this.selectedError = error;
      this.errorModalVisible = true;
    },
  },
};
</script>
<style scoped>
.form-check {
  padding-top: 1.5em;
  display: flex;
  align-items: center;
}
.lg-button {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ant-tabs-content {
  height: 100% !important;
  overflow-y: auto !important;
}
</style>
